"use client";

import { Fragment, useMemo } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { SearchBar } from "@/components/search-bar";
import { CompanyCard } from "@/components/company-card";
import { Pagination } from "@/components/pagination";
import { ScrollReveal } from "@/components/scroll-reveal";
import { parseHeadquarters } from "@/lib/location-utils";
import type { Company } from "@/lib/types";

interface CompaniesContentProps {
  companies: Company[];
  brandCounts?: Record<string, number>;
}

type SortOption = "name-asc" | "name-desc" | "brands-desc" | "brands-asc" | "updated-desc" | "updated-asc";
type FilterOption = "all" | "public" | "private";

export function CompaniesContent({ companies, brandCounts = {} }: CompaniesContentProps) {
  const searchParams = useSearchParams();
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const sortBy = (searchParams.get("sort") || "brands-desc") as SortOption;
  const filterBy = (searchParams.get("filter") || "all") as FilterOption;
  const selectedCountry = searchParams.get("country") || "all";

  const ITEMS_PER_PAGE = 100;

  // Extract unique countries from companies for the dropdown
  const uniqueCountries = useMemo(() => {
    const countrySet = new Set<string>();
    for (const company of companies) {
      if (company.headquarters) {
        const loc = parseHeadquarters(company.headquarters);
        if (loc.country) countrySet.add(loc.country);
      }
    }
    return Array.from(countrySet).sort();
  }, [companies]);

  // Helper to build URL params preserving all current filters
  const buildParams = (overrides: Record<string, string> = {}) => {
    const params = new URLSearchParams();
    const values: Record<string, string> = {
      filter: filterBy,
      country: selectedCountry,
      sort: sortBy,
      ...overrides,
    };
    if (values.filter && values.filter !== "all") params.set("filter", values.filter);
    if (values.country && values.country !== "all") params.set("country", values.country);
    if (values.sort && values.sort !== "brands-desc") params.set("sort", values.sort);
    return params.toString();
  };

  // Filter and sort companies
  const filteredAndSortedCompanies = useMemo(() => {
    let filtered = companies.filter(company => 
      company && 
      company.name && 
      company.name.trim() !== '' &&
      company.slug && 
      company.slug.trim() !== '' &&
      (brandCounts[company.slug] ?? company.brandSlugs?.length ?? 0) > 0 // Only show companies with brands
    );

    // Apply type filter
    if (filterBy !== "all") {
      filtered = filtered.filter(company => 
        company.companyType === filterBy || company.type === filterBy
      );
    }

    // Apply country filter
    if (selectedCountry !== "all") {
      filtered = filtered.filter(company => {
        if (!company.headquarters) return false;
        const loc = parseHeadquarters(company.headquarters);
        return loc.country === selectedCountry;
      });
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "name-asc":
          return a.name.localeCompare(b.name);
        case "name-desc":
          return b.name.localeCompare(a.name);
        case "brands-desc":
          return (brandCounts[b.slug] ?? b.brandSlugs?.length ?? 0) - (brandCounts[a.slug] ?? a.brandSlugs?.length ?? 0);
        case "brands-asc":
          return (brandCounts[a.slug] ?? a.brandSlugs?.length ?? 0) - (brandCounts[b.slug] ?? b.brandSlugs?.length ?? 0);
        case "updated-desc":
          return new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime();
        case "updated-asc":
          return new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime();
        default:
          return 0;
      }
    });

    return sorted;
  }, [companies, sortBy, filterBy, selectedCountry]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredAndSortedCompanies.length / ITEMS_PER_PAGE);
  const validPage = Math.min(Math.max(1, currentPage), totalPages || 1);
  const startIndex = (validPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedCompanies = filteredAndSortedCompanies.slice(startIndex, endIndex);

  // Build query params for pagination
  const queryParams: Record<string, string> = {};
  if (filterBy !== "all") queryParams.filter = filterBy;
  if (selectedCountry !== "all") queryParams.country = selectedCountry;
  if (sortBy !== "brands-desc") queryParams.sort = sortBy;

  const stats = useMemo(() => {
    const publicCompanies = companies.filter(c => 
      c.companyType === "public" || c.type === "public"
    ).length;
    const privateCompanies = companies.filter(c => 
      c.companyType === "private" || c.type === "private"
    ).length;
    const totalBrands = companies.reduce((sum, c) => sum + (brandCounts[c.slug] ?? c.brandSlugs?.length ?? 0), 0);
    
    return {
      total: companies.length,
      public: publicCompanies,
      private: privateCompanies,
      totalBrands
    };
  }, [companies]);

  return (
    <div className="py-8 md:py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="mb-12">
          <h1 className="text-3xl md:text-4xl font-serif font-semibold text-foreground">
            Parent Companies
          </h1>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed max-w-3xl">
            Explore the major corporations and conglomerates that own the
            world&apos;s most recognized brands. Each company profile includes
            ownership details, brand portfolios, and corporate information.
          </p>
        </header>

        {/* Search Bar */}
        <div className="mb-12 max-w-xl">
          <SearchBar 
            placeholder="Search for a company..." 
            initialData={{
              brands: [],
              companies: companies,
              categories: []
            }}
          />
        </div>

        {/* Filters and Sort */}
        <div className="mb-8 flex flex-col sm:flex-row gap-4 items-start sm:items-center flex-wrap">
          {/* Filter by Type */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-foreground">
              Filter by Type
            </label>
            <select
              value={filterBy}
              onChange={(e) => {
                window.location.href = `/companies?${buildParams({ filter: e.target.value })}`;
              }}
              className="px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm"
            >
              <option value="all">All Companies</option>
              <option value="public">Publicly Traded</option>
              <option value="private">Private</option>
            </select>
          </div>

          {/* Filter by Country */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-foreground">
              Filter by Country
            </label>
            <select
              value={selectedCountry}
              onChange={(e) => {
                window.location.href = `/companies?${buildParams({ country: e.target.value })}`;
              }}
              className="px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm"
            >
              <option value="all">All Countries</option>
              {uniqueCountries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>

          {/* Sort */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-foreground">
              Sort by
            </label>
            <select
              value={sortBy}
              onChange={(e) => {
                window.location.href = `/companies?${buildParams({ sort: e.target.value })}`;
              }}
              className="px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm"
            >
              <option value="brands-desc">Most Brands</option>
              <option value="brands-asc">Fewest Brands</option>
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
              <option value="updated-desc">Recently Updated</option>
              <option value="updated-asc">Oldest Updated</option>
            </select>
          </div>

          {/* Results count */}
          <div className="text-sm text-muted-foreground sm:ml-auto">
            Showing {paginatedCompanies.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, filteredAndSortedCompanies.length)} of {filteredAndSortedCompanies.length} companies
          </div>
        </div>

        {/* Stats Cards */}
        <div className="mb-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-background border border-border rounded-lg">
            <div className="text-2xl font-semibold text-accent">{stats.total}</div>
            <div className="text-sm text-muted-foreground">Total Companies</div>
          </div>
          <div className="p-4 bg-background border border-border rounded-lg">
            <div className="text-2xl font-semibold text-accent">{stats.public}</div>
            <div className="text-sm text-muted-foreground">Publicly Traded</div>
          </div>
          <div className="p-4 bg-background border border-border rounded-lg">
            <div className="text-2xl font-semibold text-accent">{stats.private}</div>
            <div className="text-sm text-muted-foreground">Private Companies</div>
          </div>
          <div className="p-4 bg-background border border-border rounded-lg">
            <div className="text-2xl font-semibold text-accent">{stats.totalBrands}</div>
            <div className="text-sm text-muted-foreground">Total Brands</div>
          </div>
        </div>

        {/* Companies Grid */}
        {paginatedCompanies.length > 0 ? (
            <>
              <ScrollReveal className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {paginatedCompanies.map((company) => (
                  <CompanyCard
                    key={company.slug}
                    company={company}
                    brandCount={brandCounts[company.slug]}
                  />
                ))}
              </ScrollReveal>

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                currentPage={validPage}
                totalPages={totalPages}
                baseUrl="/companies"
                queryParams={queryParams}
              />
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-lg text-muted-foreground">
              No companies found matching your filters.
            </p>
            <Link
              href="/companies"
              className="mt-4 inline-block px-4 py-2 text-sm font-medium bg-accent text-accent-foreground rounded-md hover:bg-accent/90 transition-colors"
            >
              Clear Filters
            </Link>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-16 p-8 bg-muted rounded-lg border border-border">
          <h2 className="text-xl font-serif font-semibold text-foreground mb-4">
            About Corporate Ownership
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Understanding corporate ownership helps consumers make informed
            decisions about the products they buy. Many well-known brands are
            owned by larger parent companies, which may influence product
            quality, pricing, and corporate policies. Our database tracks these
            ownership relationships to provide transparency about who really
            owns the brands you interact with daily.
          </p>
        </div>
      </div>
    </div>
  );
}
